import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
    ChartBarIcon,
    CheckCircleIcon,
    ExclamationTriangleIcon,
    NoSymbolIcon,
    UserGroupIcon,
    XCircleIcon,
} from "@heroicons/react/24/outline";

import { getAuditStatistics } from "../api/auditApi";
import {
    formatAdminIdentity,
    formatAuditDay,
    formatAuditLabel,
    formatPercent,
} from "../audit-utils";

// ============================================================================
// Helpers
// ============================================================================

const barColor = (idx: number) => {
    const colors = [
        "bg-blue-500",
        "bg-emerald-500",
        "bg-amber-500",
        "bg-purple-500",
        "bg-rose-500",
        "bg-cyan-500",
        "bg-pink-500",
        "bg-indigo-500",
        "bg-teal-500",
        "bg-orange-500",
        "bg-fuchsia-500",
        "bg-lime-500",
        "bg-sky-500",
        "bg-violet-500",
    ];
    return colors[idx % colors.length];
};

const statusMap: Record<string, { icon: React.ReactNode; color: string }> = {
    SUCCESS: {
        icon: <CheckCircleIcon className="h-5 w-5" />,
        color: "text-emerald-500",
    },
    FAILURE: {
        icon: <XCircleIcon className="h-5 w-5" />,
        color: "text-rose-500",
    },
    DENIED: {
        icon: <NoSymbolIcon className="h-5 w-5" />,
        color: "text-red-500",
    },
    PARTIAL: {
        icon: <ExclamationTriangleIcon className="h-5 w-5" />,
        color: "text-amber-500",
    },
};

// ============================================================================
// Component
// ============================================================================

export const AuditStatsRoute = () => {
    const today = new Date();
    const thirtyAgo = new Date(today);
    thirtyAgo.setDate(thirtyAgo.getDate() - 30);

    const [startDate, setStartDate] = useState(thirtyAgo.toISOString().split("T")[0]);
    const [endDate, setEndDate] = useState(today.toISOString().split("T")[0]);

    const { data, isLoading, error } = useQuery({
        queryKey: ["audit-statistics", startDate, endDate],
        queryFn: () => getAuditStatistics({ startDate, endDate }),
    });

    const stats = data?.data;

    // ────────────────────────────────────────────────────────────────────────
    // Loading / Error
    // ────────────────────────────────────────────────────────────────────────

    if (isLoading) {
        return (
            <div className="flex items-center justify-center py-20">
                <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary-600 border-t-transparent" />
            </div>
        );
    }

    if (error || !stats) {
        return (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 p-8 text-center dark:border-rose-800 dark:bg-rose-900/20">
                <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-rose-400" />
                <p className="mt-3 text-sm text-rose-600 dark:text-rose-400">
                    Failed to load audit statistics
                </p>
            </div>
        );
    }

    // Compute maximums for bar widths
    const maxCategoryCount = Math.max(
        ...Object.values(stats.actionsByCategory as Record<string, number>),
        1
    );
    const maxDailyCount = Math.max(
        ...stats.dailyActivity.map((d) => d.count),
        1
    );
    const maxHourlyCount = Math.max(
        ...Object.values(stats.hourlyDistribution as Record<string, number>),
        1
    );

    // ────────────────────────────────────────────────────────────────────────
    // Render
    // ────────────────────────────────────────────────────────────────────────

    return (
        <div className="space-y-6">
            {/* Date Range */}
            <div className="flex flex-wrap items-end gap-3">
                <div>
                    <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">
                        Start Date
                    </label>
                    <input
                        type="date"
                        value={startDate}
                        onChange={(e) => setStartDate(e.target.value)}
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                    />
                </div>
                <div>
                    <label className="mb-1 block text-xs text-slate-500 dark:text-slate-400">
                        End Date
                    </label>
                    <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        className="rounded-lg border border-slate-300 px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white"
                    />
                </div>
            </div>

            {/* Summary Cards */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
                {[
                    {
                        label: "Total Actions",
                        value: stats.totalActions.toLocaleString(),
                        icon: <ChartBarIcon className="h-5 w-5 text-blue-500" />,
                        bg: "bg-blue-50 dark:bg-blue-900/20",
                    },
                    {
                        label: "Unique Admins",
                        value: stats.uniqueAdmins,
                        icon: <UserGroupIcon className="h-5 w-5 text-purple-500" />,
                        bg: "bg-purple-50 dark:bg-purple-900/20",
                    },
                    {
                        label: "Successful",
                        value: stats.successfulActions.toLocaleString(),
                        icon: <CheckCircleIcon className="h-5 w-5 text-emerald-500" />,
                        bg: "bg-emerald-50 dark:bg-emerald-900/20",
                    },
                    {
                        label: "Success Rate",
                        value: formatPercent(stats.successRate),
                        icon: <ChartBarIcon className="h-5 w-5 text-amber-500" />,
                        bg: "bg-amber-50 dark:bg-amber-900/20",
                    },
                ].map((c) => (
                    <div
                        key={c.label}
                        className={`flex items-center gap-3 rounded-2xl border border-slate-200 p-4 ${c.bg} dark:border-slate-800`}
                    >
                        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/80 dark:bg-slate-800">
                            {c.icon}
                        </div>
                        <div>
                            <p className="text-xs text-slate-500 dark:text-slate-400">{c.label}</p>
                            <p className="text-lg font-bold text-slate-900 dark:text-white">
                                {c.value}
                            </p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Failed / Denied row */}
            <div className="grid gap-4 sm:grid-cols-2">
                <div className="flex items-center gap-3 rounded-2xl border border-rose-200 bg-rose-50 p-4 dark:border-rose-800 dark:bg-rose-900/20">
                    <XCircleIcon className="h-8 w-8 text-rose-500" />
                    <div>
                        <p className="text-xs text-rose-500">Failed Actions</p>
                        <p className="text-lg font-bold text-rose-700 dark:text-rose-400">
                            {stats.failedActions.toLocaleString()}
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3 rounded-2xl border border-red-200 bg-red-50 p-4 dark:border-red-800 dark:bg-red-900/20">
                    <NoSymbolIcon className="h-8 w-8 text-red-500" />
                    <div>
                        <p className="text-xs text-red-500">Denied Actions</p>
                        <p className="text-lg font-bold text-red-700 dark:text-red-400">
                            {stats.deniedActions.toLocaleString()}
                        </p>
                    </div>
                </div>
            </div>

            {/* Two-Column: Categories + Status */}
            <div className="grid gap-6 lg:grid-cols-2">
                {/* Actions by Category */}
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-4 font-semibold text-slate-900 dark:text-white">
                        Actions by Category
                    </h3>
                    <div className="space-y-3">
                        {Object.entries(stats.actionsByCategory as Record<string, number>)
                            .sort(([, a], [, b]) => b - a)
                            .map(([cat, count], idx) => (
                                <div key={cat}>
                                    <div className="mb-1 flex justify-between text-xs">
                                        <span className="text-slate-600 dark:text-slate-300">
                                            {formatAuditLabel(cat)}
                                        </span>
                                        <span className="font-medium text-slate-900 dark:text-white">
                                            {count}
                                        </span>
                                    </div>
                                    <div className="h-2 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                                        <div
                                            className={`h-full rounded-full ${barColor(idx)}`}
                                            style={{
                                                width: `${(count / maxCategoryCount) * 100}%`,
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                    </div>
                </div>

                {/* Actions by Status */}
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-4 font-semibold text-slate-900 dark:text-white">
                        Actions by Status
                    </h3>
                    <div className="space-y-4">
                        {Object.entries(stats.actionsByStatus as Record<string, number>).map(
                            ([status, count]) => {
                                const meta = statusMap[status] ?? statusMap.SUCCESS;
                                return (
                                    <div key={status} className="flex items-center gap-3">
                                        <span className={meta.color}>{meta.icon}</span>
                                        <div className="flex-1">
                                            <span className="text-sm text-slate-700 dark:text-slate-300">
                                                {formatAuditLabel(status)}
                                            </span>
                                        </div>
                                        <span className="text-sm font-bold text-slate-900 dark:text-white">
                                            {count.toLocaleString()}
                                        </span>
                                    </div>
                                );
                            }
                        )}
                    </div>
                </div>
            </div>

            {/* Two-Column: Top Actions + Most Active */}
            <div className="grid gap-6 lg:grid-cols-2">
                {/* Top Actions */}
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-3 font-semibold text-slate-900 dark:text-white">
                        Top Actions
                    </h3>
                    <table className="w-full text-sm">
                        <thead className="text-xs text-slate-500 dark:text-slate-400">
                            <tr>
                                <th className="pb-2 text-left">Action</th>
                                <th className="pb-2 text-right">Count</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {stats.topActions.map((a) => (
                                <tr key={a.action}>
                                    <td className="py-2 text-slate-700 dark:text-slate-300">
                                        {formatAuditLabel(a.action)}
                                    </td>
                                    <td className="py-2 text-right font-medium text-slate-900 dark:text-white">
                                        {a.count}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Most Active Admins */}
                <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                    <h3 className="mb-3 font-semibold text-slate-900 dark:text-white">
                        Most Active Admins
                    </h3>
                    <table className="w-full text-sm">
                        <thead className="text-xs text-slate-500 dark:text-slate-400">
                            <tr>
                                <th className="pb-2 text-left">Admin</th>
                                <th className="pb-2 text-right">Actions</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                            {stats.mostActiveAdmins.map((a) => (
                                <tr key={a.adminEmail}>
                                    <td className="py-2 text-slate-700 dark:text-slate-300">
                                        <div>
                                            <p>{formatAdminIdentity(a.adminEmail, a.adminId)}</p>
                                            {a.adminEmail ? null : (
                                                <p className="mt-0.5 font-mono text-[10px] text-slate-400">
                                                    {a.adminId}
                                                </p>
                                            )}
                                        </div>
                                    </td>
                                    <td className="py-2 text-right font-medium text-slate-900 dark:text-white">
                                        {a.actionCount}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Daily Activity */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                <h3 className="mb-4 font-semibold text-slate-900 dark:text-white">
                    Daily Activity
                </h3>
                <div className="space-y-2">
                    {stats.dailyActivity.map((d) => (
                        <div key={d.date} className="flex items-center gap-3">
                            <span className="w-24 shrink-0 text-xs text-slate-500 dark:text-slate-400">
                                {formatAuditDay(d.date)}
                            </span>
                            <div className="h-3 flex-1 overflow-hidden rounded-full bg-slate-100 dark:bg-slate-800">
                                <div
                                    className="h-full rounded-full bg-primary-500"
                                    style={{
                                        width: `${(d.count / maxDailyCount) * 100}%`,
                                    }}
                                />
                            </div>
                            <span className="w-12 text-right text-xs font-medium text-slate-700 dark:text-slate-300">
                                {d.count}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Hourly Distribution */}
            <div className="rounded-2xl border border-slate-200 bg-white p-5 dark:border-slate-800 dark:bg-slate-900">
                <h3 className="mb-4 font-semibold text-slate-900 dark:text-white">
                    Hourly Distribution
                </h3>
                <div className="grid grid-cols-4 gap-3 sm:grid-cols-6 lg:grid-cols-8">
                    {Array.from({ length: 24 }, (_, h) => {
                        const key = String(h);
                        const count =
                            (stats.hourlyDistribution as Record<string, number>)[key] ?? 0;
                        const intensity = maxHourlyCount
                            ? Math.max(0.1, count / maxHourlyCount)
                            : 0;
                        return (
                            <div
                                key={h}
                                className="flex flex-col items-center rounded-xl border border-slate-200 px-2 py-3 dark:border-slate-700"
                            >
                                <span className="text-[10px] text-slate-400">
                                    {h.toString().padStart(2, "0")}:00
                                </span>
                                <div
                                    className="mt-1 h-6 w-6 rounded-md"
                                    style={{
                                        backgroundColor: `rgba(99, 102, 241, ${intensity})`,
                                    }}
                                />
                                <span className="mt-1 text-xs font-medium text-slate-700 dark:text-slate-300">
                                    {count}
                                </span>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};
